import { ChatRoomLastMessageInfo, Message, QueryFilter } from '@dinosband/bv-messenger-schema'
import { MESSAGE_ADDED, MESSAGE_UPDATED, MESSAGE_DELETED, SubscriptionManager, _isInvalidMessage, filterOutInvalidMessages } from '@dinosband/bv-data-center'
import { delay, _last } from '@dinosband/dbi-utils'

import { dataCenter, messengerStore } from '../..'
import { ChatRoomStore, ChatRoomContext } from '../../controller-pool'
import { MessagesChunkSize } from '..'

import { getMessages } from './get-messages'
import { isOverlapped, merge } from './utils'
import { MessagePreloader } from './message-preloader'

const _DEBUG_LOG = process.env.NODE_ENV !== 'production' &&
                   !!process.env.VUE_APP_MESSENGER_DEBUG_LOG

interface MessageLoaderObserver {
  onNewMessage: ((message: Message) => void)
  onUpdateMessage: ((message: Message) => void)
  willReconnect: (() => void)
  didReconnect: (() => void)
  willLoadMessages: (() => void)
  didLoadMessages: (() => void)
  willEnterBackground: (() => void)
  willEnterForeground: (() => void)
  didEnterForeground: (() => void)
}

export enum MessageLoaderStatus {
  UNDEFINED = 'undefined',
  INITIALIZED = 'initialized',
  LOADING = 'loading',
  LOADED = 'loaded',
}

export class MessageLoader {

  chatRoomId: number
  pageSize = MessagesChunkSize
  cursor = 0
  status = MessageLoaderStatus.UNDEFINED
  isUpToDate = false
  filter?: QueryFilter

  private store: ChatRoomStore
  private preloader?: MessagePreloader
  private messages: Message[] = []
  private prevMessages: Message[] = []
  private fragmented = true
  private appIsActive = true
  private preloaded = false
  private context: ChatRoomContext
  private useCache = true
  private searchMode = false
  private subscriptions = new SubscriptionManager()
  private observers: MessageLoaderObserver[] = []

  constructor (context: ChatRoomContext, searchMode = false) {
    this.chatRoomId = context.chatRoomId
    this.context = context
    this.searchMode = searchMode
    this.useCache = !searchMode
    const storePath = `chatRoom:${this.chatRoomId}`
    this.store = context.store
    if (!searchMode) {
      const controller = messengerStore.chatRoomController(this.chatRoomId)
      // this.preloader = controller.messagePreloader
      this.watchLastMessage(storePath)
      this.subscriptions.init(dataCenter.context.pubsub)
      this.subscriptions.subscribe(MESSAGE_ADDED, (i) => {
        if (i.chatRoomId === this.chatRoomId) {
          this.messageDidAdd(i)
        }
      })
      this.subscriptions.subscribe(MESSAGE_UPDATED, (payload: { message: Message, valid: boolean}) => {
        const i = payload.message
        if (i.chatRoomId === this.chatRoomId) {
          this.messageDidUpdate(i)
        }
      })
      this.subscriptions.subscribe(MESSAGE_DELETED, (i) => {
        if (i.chatRoomId === this.chatRoomId) {
          this.messageDidUpdate(i)
        }
      })
      controller.registerLoader(this)
    }
    this.status = MessageLoaderStatus.INITIALIZED
  }

  deinit () {
    messengerStore.chatRoomController(this.chatRoomId)?.deregisterLoader(this)
    this.subscriptions.cleanup()
    this.unwatchMessages?.()
    this.unwatchMessages = undefined
    this.unwatchLastMessage?.()
    this.unwatchLastMessage = undefined
    this.unwatchAppState?.()
    this.unwatchAppState = undefined
  }

  get loaded (): { cursor: number, messages: Message[] } {
    return {
      cursor: this.cursor,
      messages: this.appIsActive ? this.messages : this.prevMessages,
    }
  }

  private preloadsContains (messageId?: number) {
    if (!this.preloader) {
      return false
    }
    const preload = this.preloader.messages
    if (messageId) {
      return preload.find((i) => i.id == messageId)
    } else {
      return _last(preload)?.id == this.context.lastMessage?.id
    }
  }

  private getPreloads (messageId?: number) {
    if (!messageId || this.store.messages.find((i) => i.id == messageId)) {
      this.isUpToDate = true
      this.replaceMessages(this.store.messages)
    } else {
      // await preloader.wait()
      if (this.preloader && this.preloadsContains(messageId)) {
        const preloads = this.preloader.messages
        this.preloaded = true
        this.replaceMessages(preloads)
        // setTimeout(() => {
        //   this.observers.forEach((i) => i.willReconnect())
        // }, 0.05)
      }
    }
  }

  async reset (startMessageSeq?: number, startMessageId?: number, filter?: QueryFilter) {
    this.filter = filter
    this.messages = []
    this.preloaded = false
    this.fragmented = true
    if (!this.useCache) {
      await this.loadMessages('recent')
      return
    }
    this.getPreloads(startMessageId)
    if (this.isUpToDate) {
      return
    }
    if (this.preloaded) {
      const last = _last(this.messages)
      const latest = this.context.lastMessage
      if (last && latest && latest.seq - MessagesChunkSize <= last.seq) {
        this.isUpToDate = true
        return
      }
    }
    if (startMessageId) {
      await this.loadMessages('around', { id: startMessageId, seq: startMessageSeq })
    }
  }

  messageDidAdd (message: Message) {
    if (!this.isUpToDate) {
      return
    }
    const last = _last(this.messages)?.seq ?? 0
    if (message.seq < last || last + 1 < message.seq) {
      this.fragmented = true
    }
    if (last < message.seq) {
      this.addMessages([message], true)
    }
    this.notifyNewMessage(message)
    this.fillMessageSummaries([message])
    this.increaseCursor()
  }

  messageDidUpdate (message: Message) {
    if (!message) {
      this.increaseCursor() // when ChatRoomState.{messages, lastMessage} is empty
      return
    }
    let index = this.messages.findIndex((i) => message.id === i.id)
    const { userId, blockedProfileIds } = this.context.mainStore
    const isInvalid = _isInvalidMessage(message, userId, this.filter?.excludeDeleted, blockedProfileIds)
    if (0 <= index) {
      if (isInvalid) {
        this.messages.splice(index, 1)
      } else {
        this.messages[index] = message
      }
    } else if (!isInvalid) {
      index = this.messages.findIndex((i) => message.id < i.id)
      if (index == 0) { // 0은 동떨어진 것일 수 있으므로 버린다.
        return
      }
      if (0 < index) {
        this.messages.splice(index, 0, message)
      } else if (this.isUpToDate) {
        this.messages.push(message)
        this.notifyNewMessage(message)
      } else {
        return
      }
      this.fillMessageSummaries([message])
    }
    this.observers.forEach((i) => i.onUpdateMessage(message))
    this.increaseCursor()
  }

  async willReconnect () {
    // this.isUpToDate = false
    this.observers.forEach((i)=> i.willReconnect())
  }

  async didReconnect (added?: ChatRoomLastMessageInfo, updated?: ChatRoomLastMessageInfo) {
    if (updated) {
      const last = this.store.userView?.lastMessageId ?? undefined
      this.reset(last)
    } else if (added) {
      if (isOverlapped(this.store.messages, this.messages)) {
        this.isUpToDate = true
        this.mergeMessages(this.store.messages)
        this.increaseCursor()
      } else {
        const last = _last(this.messages)
        await this.loadMessages('after', last)
      }
    }
    this.fillMessageSummaries(this.messages)
    this.observers.forEach((i) => i.didReconnect())
  }

  willEnterBackground () {
    this.observers.forEach((i) => i.willEnterBackground())
  }

  willEnterForeground () {
    this.observers.forEach((i) => i.willEnterForeground())
  }

  didEnterForeground () {
    this.observers.forEach((i) => i.didEnterForeground())
  }

  registerCallback (observer: MessageLoaderObserver) {
    this.observers.push(observer)
  }

  unregisterCallback (observer: MessageLoaderObserver) {
    this.observers = this.observers.filter((i) => i != observer)
  }

  private unwatchMessages?: () => void
  private unwatchLastMessage?: () => void
  private unwatchAppState?: () => void
  private async watchLastMessage (storePath: string) {
    const state = this.store.state
    this.unwatchMessages = this.store.watch(() => state.messages, (value) => {
      if (value.length == 0) {
        // console.error('[message-loader]', 'watchMessages', 'empty messages', value.length, this.messages.length, this.isUpToDate, this.fragmented)
        return
      }
      if (this.fragmented || this.preloaded || this.messages.length < value.length) {
        if (this.isUpToDate) {
          this.replaceMessages(value)
        } else if (isOverlapped(value, this.messages)) {
          this.insertMessages(value)
          this.isUpToDate = true
        }
        this.fragmented = false
        this.preloaded = false
      }
    })
    this.unwatchAppState = this.store.watch((state) => state.appState.isActive, (value) => {
      this.appIsActive = value
      if (value) {
        this.willEnterForeground()
        this.increaseCursor()
        this.didEnterForeground()
      } else {
        this.prevMessages = [...this.messages]
        this.willEnterBackground()
      }
    })
  }

  async loadMessages (direction: 'before' | 'after' | 'around' | 'recent', message?: { seq?: number, id?: number}) {
    this.notifyLoadingStatus(MessageLoaderStatus.LOADING)
    const useCache_ = this.useCache && !this.context.isFiltered
    const result = await getMessages(this.chatRoomId, direction, message, useCache_, this.filter)
    if (!result?.length) {
      this.notifyLoadingStatus(MessageLoaderStatus.LOADED)
      return 0
    }
    if (direction == 'around') {
      this.isUpToDate = false
      this.fragmented = true
    }
    this.addMessages(result, direction != 'around')
    await this.fillMessageSummaries(result)
    this.increaseCursor()
    this.notifyLoadingStatus(MessageLoaderStatus.LOADED)
    return result.length
  }

  private async notifyLoadingStatus (status: MessageLoaderStatus) {
    if (status === MessageLoaderStatus.LOADING) {
      this.status = status
      this.observers.forEach((i) => i.willLoadMessages())
    } else if (status === MessageLoaderStatus.LOADED) {
      await delay(100)
      this.status = status
      this.observers.forEach((i) => i.didLoadMessages())
    }
  }

  private addMessages (messages: Message[], continuous = false) {
    if (continuous) {
      this.mergeMessages(messages)
    } else {
      this.messages = [...messages]
    }
    if (this.isUpToDate || this.searchMode) {
      return
    }

    const liveMessages = this.store.messages
    if (!isOverlapped(this.messages, liveMessages)) {
      return
    }
    this.mergeMessages(liveMessages)
    this.isUpToDate = true
  }

  private mergeMessages (messages: Message[]) {
    if (messages.length == 0) {
      return
    }
    this.messages = [...merge(this.messages, messages, true)]
  }

  private insertMessages (messages: Message[]) {
    let result = [...this.messages]
    for (const message of messages) {
      const exist = result.find((i) => i.id == message.id)
      if (exist) {
        if (message.updatedAt && (!exist.updatedAt || exist.updatedAt < message.updatedAt)) {
          this.messageDidUpdate(message)
          result = [...this.messages]
        }
        continue
      }
      const index = result.findIndex((i) => message.id < i.id)
      if (index < 0) {
        result.push(message)
      } else {
        result.splice(index, 0, message)
      }
    }
    if (result.length != this.messages.length) {
      this.setMessages(result)
      this.increaseCursor()
    }
    this.fillMessageSummaries(messages)
  }

  private replaceMessages (messages: Message[]) {
    this.setMessages(messages)
    this.fillMessageSummaries(messages)
    this.increaseCursor()
  }

  private notifyNewMessage (message: Message) {
    this.observers.forEach((i) => i.onNewMessage(message))
  }

  private setMessages (messages: Message[]) {
    const filtered = filterOutInvalidMessages(messages, this.filter?.excludeDeleted)
    this.messages = filtered
  }

  private async fillMessageSummaries (messages: Message[]) {
    if (!this.context.isJoined) {
      return
    }
    const summaries = this.store.messageSummaries
    const messageIds = messages.map((i) => i.id).filter((i) => !summaries[i])
    if (!messageIds.length) {
      return
    }
    const result = await this.context.mainStore.readMessageSummaries(messageIds)
    if (!result.length) {
      return
    }
    this.store.addMessageSummaries(result)
    const commentsChatRoomIds = result.flatMap((i) => i.commentsChatRoomId ?? [])
    const lastMessages = await dataCenter.messages.lastCommentMessages.readMany(commentsChatRoomIds, true)
    // const chatRoomInfos: ChatRoomLastMessageInfo[] = lastMessages.map(i => { chatRoomId: i.chatRoomId, lastMessage: i }})
    this.store.addCommentsChatRoomInfos(lastMessages)
  }

  private increaseCursor () {
    if (!this.appIsActive) {
      return
    }
    this.cursor++
  }
}

